import {KpisList, type AlternativeFuelRates, type PerformanceDescendantWithId} from '@domain/kpis'
import {HierarchyNodeMetadata} from '@domain/shared'
import {DateRange} from '@hconnect/uikit/src/common'
import {Paper, Stack} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {generateBarChartData} from '../components/actualVsPlanned.columns'
import {AcVsPlChartPie} from '../components/AcVsPlChartPie'
import {BarChart} from '../components/charts'
import {MobileLocationCards} from '../components/MobileLocationCards'
import {FEATURE_NAMESPACE} from '../consts'
import {
  ActualVsPlannedRoutes,
  AggregatedKpiData,
  BarChartData,
  Comment,
  CommentLocationData
} from '../types'

type Props = {
  data: AggregatedKpiData
  hasQuarterlyData: boolean
  currentTab: KpisList
  hideCharts: boolean
  dateRange: DateRange<Date>
  setCommentLocationData: (id: CommentLocationData) => void
  comments: Comment[]
  hierarchy?: HierarchyNodeMetadata
  onLocationClick: (item: PerformanceDescendantWithId<AggregatedKpiData>) => void
  getCountryHeaderLink?: ActualVsPlannedRoutes['countryHeader']
}

export const MobileAcVsPlContainer = ({
  data,
  hasQuarterlyData,
  currentTab,
  hideCharts = false,
  dateRange,
  setCommentLocationData,
  comments,
  hierarchy,
  onLocationClick,
  getCountryHeaderLink
}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)

  const barChartData: BarChartData = useMemo(
    () =>
      generateBarChartData({
        data,
        dateRange,
        currentTab,
        translationFn: (key: string, inputParams: {[key: string]: string} = {}) =>
          t(key, inputParams),
        locale: i18n.language
      }),
    [data, dateRange, i18n.language, t, currentTab]
  )

  return (
    <Stack gap={2}>
      {!hideCharts && (
        <Paper sx={{padding: 2}}>
          {currentTab !== KpisList.AlternativeFuelRate && <BarChart data={barChartData} />}

          {currentTab === KpisList.AlternativeFuelRate && (
            // Note: this is shown only for AlternativeFuelRates and hence types are mapped
            <AcVsPlChartPie data={data as unknown as AlternativeFuelRates} dateRange={dateRange} />
          )}
        </Paper>
      )}
      <MobileLocationCards
        data={data}
        hasQuarterlyData={hasQuarterlyData}
        currentTab={currentTab}
        dateRange={dateRange}
        setCommentLocationData={setCommentLocationData}
        comments={comments}
        hierarchy={hierarchy}
        onLocationClick={onLocationClick}
        getCountryHeaderLink={getCountryHeaderLink}
      />
    </Stack>
  )
}
